import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Shield, Cloud, Bot, BarChart3 } from "lucide-react";
import { useTranslation } from "@/i18n/useTranslation";

const icons = [Cloud, Bot, Shield, BarChart3];
const statKeys = ["statAzure", "statCopilot", "statSecurity", "statOkr"] as const;

const ease = [0.16, 1, 0.3, 1] as const;

const HeroSection = () => {
  const { t } = useTranslation();

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const stats = statKeys.map((k, i) => ({ label: t.hero[k], icon: icons[i] }));

  return (
    <section
      id="hero"
      className="relative min-h-[92vh] flex items-center overflow-hidden pt-16"
      style={{ background: "var(--hero-gradient)" }}
    >
      <div
        className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[700px] rounded-full opacity-[0.07]"
        style={{
          background: "radial-gradient(circle, hsl(24 94% 53%), transparent 70%)",
        }}
      />

      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease }}
          className="max-w-3xl"
        >
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, ease }}
            className="inline-flex items-center gap-2 rounded-full border border-border/60 glass px-4 py-1.5 mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-primary" />
            <span className="text-sm text-muted-foreground font-body">
              {t.hero.badge}
            </span>
          </motion.div>

          <h1 className="font-display font-bold text-foreground mb-6">
            {t.hero.heading}{" "}
            <span className="text-gradient">{t.hero.headingAccent}</span>
          </h1>

          <p className="text-lg text-muted-foreground max-w-xl mb-10 leading-relaxed font-body">
            {t.hero.description}
          </p>

          <div className="flex flex-wrap gap-4 mb-16">
            <Button size="lg" variant="premium" onClick={() => scrollTo("contact")}>
              {t.hero.cta}
              <ArrowRight size={18} />
            </Button>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, ease }}
            className="flex flex-wrap gap-6 sm:gap-10"
          >
            {stats.map((s) => (
              <div key={s.label} className="flex items-center gap-2.5 group">
                <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <s.icon size={16} className="text-primary" />
                </div>
                <span className="text-sm font-medium text-muted-foreground font-body">
                  {s.label}
                </span>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
